import React, { useEffect, useMemo, useRef } from 'react';
import { useParams, useSearchParams } from 'next/navigation';
import { Category } from 'shared/types/product';
import { useTranslations } from 'use-intl';
import Button from 'components/commercetools-ui/atoms/button';
import { ImageProps } from 'components/commercetools-ui/atoms/image';
import ProductGridWrapper from 'components/HOC/product-banner-wrapper/components/banner-products-grid';
import { mapProductToGA4Item, trackSelectItem, trackViewItemList } from 'helpers/analytics/ga4Events';
import useMediaQuery from 'helpers/hooks/useMediaQuery';
import { mediumDesktop } from 'helpers/utils/screensizes';
import { useFeatures } from 'providers/feature-flag';
import { Product } from 'types/entity/product';
import { Reference } from 'types/reference';
import { useProjectSettings, useWishlist } from 'frontastic';
import { TieredPriceData } from 'frontastic/tastics/products/details';
import { useProductList } from '../../context';
import { SortConfiguration } from '../../types';
import CurrentRefinements from '../current-refinements';
import CustomFacets from '../customfacets';
import PlpProductTileWrapper from '../plp-product-tile-wrapper';
import { groupVariantsByAttributes } from './helper';

interface Props {
  products: Product[];
  paginationText?: string;
  tileCTA?: string;
  categories?: Category[];
  config?: Record<string, any>;

  textConfig?: Record<string, string>;
  tieredPrice?: TieredPriceData[];
  calendarOptions?: any;
  sortConfiguration?: SortConfiguration[];
  setOpenFacets?: any;
  openFacets?: boolean;
  viewCategoryFacets?: any;
  viewDynamicFacets?: any;
  primaryLogoLight?: ImageProps;
  primaryLogoDark?: ImageProps;
  primaryLogoLink?: Reference;
  primaryLogoLinkMobile?: Reference;
  primaryLogoMobileDark?: ImageProps;
  primaryLogoMobileLight?: ImageProps;
  tagsToShow?: string;
  facetLoadMoreText?: string;
  facetSeeLessText?: string;
  noResultsPlP?: string;
  isToggleP2r?: boolean;
}

export type Term = {
  identifier: string;
  label: string;
  count: number;
  key: string;
  sourceIdentifier?: string;
};
export type GroupedAttribute = {
  type: string;
  identifier: string;
  label: string;
  key: string;
  selected: boolean;
  terms: Term[];
};

const List: React.FC<Props> = ({
  products,
  paginationText,
  tileCTA,
  categories,
  config,
  textConfig,
  tieredPrice,
  calendarOptions,
  sortConfiguration,
  openFacets,
  viewCategoryFacets,
  viewDynamicFacets,
  setOpenFacets,
  primaryLogoLight,
  primaryLogoDark,
  primaryLogoLink,
  primaryLogoLinkMobile,
  primaryLogoMobileDark,
  primaryLogoMobileLight,
  tagsToShow,
  facetLoadMoreText,
  facetSeeLessText,
  noResultsPlP,
  isToggleP2r,
}) => {
  const translate = useTranslations();
  const [isDesktopSize] = useMediaQuery(mediumDesktop);
  const { searchQuery, loadMore, totalItems, facetsConfiguration } = useProductList();
  const { slug = [] } = useParams();
  const { projectSettings } = useProjectSettings();

  const currentSlug = slug.at(-1);
  const shouldResolveCategory = !searchQuery && slug.length <= 1;

  const category = shouldResolveCategory
    ? (categories?.find((category) => category.slug === currentSlug) ?? null)
    : null;

  const matchedCategorySettings = projectSettings?.categories?.find(
    (categorySetting) => categorySetting.categoryKey === category?.categoryKey,
  );

  const shouldShowCatFilterFirst = matchedCategorySettings?.shouldShowCatFilterFirst ?? false;

  const { data: wishlist } = useWishlist();
  const [srMessage, setSrMessage] = React.useState('');
  const firstTileRef = useRef<HTMLDivElement | null>(null);
  const shouldFocusRef = useRef(false);
  const prevLengthRef = useRef(0);

  const loadedAll = useMemo(() => products.length < totalItems, [products, totalItems]);
  const groupedAttributes = groupVariantsByAttributes(
    facetsConfiguration,
    viewDynamicFacets?.[0]['facetsGroup'],
    viewCategoryFacets?.[0]['facetsGroup'],
    wishlist,
    Boolean(searchQuery) || shouldShowCatFilterFirst,
  );
  const searchParams = useSearchParams();
  // GA4: view_item_list — fire when products are loaded/changed
  const viewItemListFired = useRef<string>('');
  useEffect(() => {
    if (products.length > 0) {
      const productIds = products.map((p) => p.productId).join(',');
      if (productIds !== viewItemListFired.current) {
        const listName = searchQuery ? 'Search Results Page' : 'Product List Page';
        const items = products.map((product, index) =>
          mapProductToGA4Item(enableGA4(), product, { index, itemListName: listName }),
        );
        trackViewItemList(listName, items);
        viewItemListFired.current = productIds;
      }
    }
  }, [products, searchQuery]);

  const hasTerms = groupedAttributes.some((item) => item.terms && item.terms.length > 0);
  const parentGridCols =
    openFacets && isDesktopSize && hasTerms ? 'grid-cols-1 lg:grid-cols-[210px,1fr]' : 'grid-cols-1';

  const { enableGA4 } = useFeatures();

  useEffect(() => {
    if (shouldFocusRef.current && firstTileRef.current) {
      const el = firstTileRef.current.querySelector('a, button') as HTMLElement;

      el?.focus();

      shouldFocusRef.current = false;
      setSrMessage('');
    }
  }, [products]);

  useEffect(() => {
    prevLengthRef.current = products.length;
  }, [products.length]);

  return (
    (products.length > 0 || Boolean(searchParams.get('wishListIds[0]'))) && (
      <div className="mx-12 lg:mx-20">
        <div className={`grid ${parentGridCols} gap-12 lg:gap-0`}>
          {openFacets && isDesktopSize ? (
            <aside id="filters-panel" className="h-90vh hidden self-start pl-0 lg:block">
              <CustomFacets
                tagsToShow={tagsToShow}
                facetLoadMoreText={facetLoadMoreText}
                facetSeeLessText={facetSeeLessText}
                categories={categories}
                setOpenFacets={setOpenFacets}
                openFacets={openFacets}
                sortConfiguration={sortConfiguration}
                primaryLogoLight={primaryLogoLight}
                primaryLogoDark={primaryLogoDark}
                primaryLogoLink={primaryLogoLink}
                primaryLogoLinkMobile={primaryLogoLinkMobile}
                primaryLogoMobileDark={primaryLogoMobileDark}
                primaryLogoMobileLight={primaryLogoMobileLight}
                groupedAttributes={groupedAttributes}
              />
            </aside>
          ) : (
            <CustomFacets
              tagsToShow={tagsToShow}
              facetLoadMoreText={facetLoadMoreText}
              facetSeeLessText={facetSeeLessText}
              categories={categories}
              setOpenFacets={setOpenFacets}
              openFacets={openFacets}
              sortConfiguration={sortConfiguration}
              primaryLogoLight={primaryLogoLight}
              primaryLogoDark={primaryLogoDark}
              primaryLogoLink={primaryLogoLink}
              primaryLogoLinkMobile={primaryLogoLinkMobile}
              primaryLogoMobileDark={primaryLogoMobileDark}
              primaryLogoMobileLight={primaryLogoMobileLight}
              groupedAttributes={groupedAttributes}
            />
          )}

          <section className="container">
            <div aria-live="polite" role="status" className="sr-only">
              {srMessage}
            </div>

            {/* chips */}
            <CurrentRefinements textConfig={textConfig} />
            {products.length === 0 ? (
              <h3
                data-ffid="lbl-no-results-found-text"
                className="mt-4 p-20 pb-0 text-center text-18 text-gray-500 lg:text-28"
              >
                {noResultsPlP}
              </h3>
            ) : (
              <ProductGridWrapper className="py-24 lg:pb-36 lg:pt-30" centerInsufficientColumns={false}>
                {products.map((product, index) => (
                  <div
                    key={product.productId}
                    ref={index === prevLengthRef.current ? firstTileRef : null}
                    tabIndex={-1}
                    style={{ display: 'contents' }}
                  >
                    <PlpProductTileWrapper
                      product={product}
                      isSearchResult={!!searchQuery}
                      onClick={() => {
                        // GA4: select_item
                        const listName = searchQuery ? 'Search Results Page' : 'Product List Page';
                        trackSelectItem(listName, [
                          mapProductToGA4Item(enableGA4(), product, { index, itemListName: listName }),
                        ]);
                      }}
                      index={index}
                      tileCTA={tileCTA}
                      categories={categories}
                      config={config}
                      textConfig={textConfig}
                      tieredPrice={tieredPrice}
                      calendarOptions={calendarOptions}
                      isToggleP2r={isToggleP2r}
                    />
                  </div>
                ))}
              </ProductGridWrapper>
            )}
            {/* Load more */}
            {loadedAll && (
              <Button
                variant="ghost"
                className="mx-auto block rounded-2xl border-2 border-green-800 px-52 py-8 text-16 font-normal text-green-800 hover:border-[#349D82] hover:text-[#349D82]"
                onClick={() => {
                  setSrMessage(translate('product.loading-more'));
                  shouldFocusRef.current = true;
                  prevLengthRef.current = products.length;
                  loadMore();
                }}
                data-ffid="btn-load-more"
              >
                {paginationText ?? translate('product.load-more')}
              </Button>
            )}
          </section>
        </div>
      </div>
    )
  );
};

export default List;
